import { HttpClient } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { marker } from "@jsverse/transloco-keys-manager/marker";
import { isArray, isUndefined, omitBy } from "lodash-es";
import { Observable, map } from "rxjs";
import { UserNotify } from "src/app/support/notify-decorator";
import { PageRequest } from "src/app/support/page-request";
import { PaginatedResult } from "src/app/support/paginated-result";
import { environment } from "src/environments/environment";
import { Tag, TagCreate, TagLight, TagUpdate } from "../../models/tag/tag";
import { omitUndefined } from "src/app/support/object";

@Injectable()
export class TagBackend {
  constructor(private http: HttpClient) {}

  @UserNotify({
    successMessage: marker("Tag creato"),
    errorMessage: marker("Impossibile creare il tag")
  })
  create(request: TagCreate): Observable<Tag> {
    return this.http.post<Tag>(`${environment.snoots.api.tag}/tags`, request);
  }

  list(
    request: PageRequest,
    category?: string,
    tenant?: string
  ): Observable<PaginatedResult<TagLight>> {
    return this.http.get<PaginatedResult<TagLight>>(
      `${environment.snoots.api.tag}/tags`,
      {
        params: omitBy<any>(
          {
            p: request.page,
            s: request.pageSize,
            category: category,
            light: true,
            tenant,
            ...request.filterValues
          },
          isUndefined
        )
      }
    );
  }

  listAllLight({
    category,
    tenant
  }: {
    category: string;
    tenant?: string;
  }): Observable<TagLight[]> {
    return this.list(
      {
        page: 1,
        pageSize: 10000
      },
      category,
      tenant
    ).pipe(map((list) => list.docs));
  }

  searchAll(name: string, category: string): Observable<TagLight[]> {
    return this.search(name, category, { page: 1, pageSize: 100000 });
  }

  search(
    name: string,
    category: string,
    options?: { tenant?: string; page: number; pageSize: number }
  ): Observable<TagLight[]> {
    const payload: { category: string; q?: string } = {
      category: category
    };

    if (name) {
      payload.q = name;
    }

    return this.http
      .get<PaginatedResult<Tag>>(`${environment.snoots.api.tag}/tags`, {
        params: omitUndefined({
          ...payload,
          light: true,
          tenant: options?.tenant,
          p: options?.page,
          s: options?.pageSize
        })
      })
      .pipe(
        map((result) => {
          if (isArray(result)) {
            return result;
          }

          return result.docs;
        })
      );
  }

  loadAll(name: string, category: string): Observable<TagLight[]> {
    const payload: { category: string; q?: string } = {
      category: category
    };

    if (name) {
      payload.q = name;
    }

    return this.http
      .get<PaginatedResult<Tag>>(`${environment.snoots.api.tag}/tags`, {
        params: {
          p: 1,
          s: 1000,
          ...payload,
          light: true
        }
      })
      .pipe(
        map((result) => {
          if (isArray(result)) {
            return result;
          }

          return result.docs;
        })
      );
  }

  load(id: string): Observable<Tag> {
    return this.http.get<Tag>(`${environment.snoots.api.tag}/tags/${id}`);
  }

  loadLight(id: string): Observable<TagLight> {
    return this.http.get<Tag>(`${environment.snoots.api.tag}/tags/${id}`, {
      params: {
        light: true
      }
    });
  }

  loadByName(name: string): Observable<Tag[]> {
    return this.http.get<Tag[]>(`${environment.snoots.api.tag}/tags`, {
      params: {
        name: name
      }
    });
  }

  @UserNotify({
    successMessage: marker("Tag aggiornato"),
    errorMessage: marker("Impossibile aggiornare il tag")
  })
  update(id: string, request: Partial<TagUpdate>): Observable<Tag> {
    return this.http.patch<Tag>(
      `${environment.snoots.api.tag}/tags/${id}`,
      request
    );
  }

  @UserNotify({
    successMessage: marker("Tag cancellato"),
    errorMessage: marker("Impossibile canellare il tag")
  })
  delete(id: string): Observable<string> {
    return this.http.delete<string>(`${environment.snoots.api.tag}/tags/${id}`);
  }
}
